import {
  setActiveIframe,
  staticState,
  useHostLocalStorage,
  useHostStoreConnect,
} from '@wellesley/host-remote'
import {
  useHostFetch,
  baseUrl,
  useHostOpenapiHydrationCache,
} from '@wellesley/openapi'
import {
  createContext,
  FC,
  forwardRef,
  memo,
  useContext,
  useEffect,
  useRef,
} from 'react'
import { RemoteAppProps } from '../types'
import { AppLoader } from 'components/AppLoader'
import { cn } from '@wellesley/ui'
type IframeAppProps = RemoteAppProps & {
  className?: string
  onLoad?: () => void
}

const IframeAppForwardRef = forwardRef<HTMLIFrameElement, IframeAppProps>(
  ({ id, src, name = 'Remote App', className, onLoad }, ref) => {
    const iframeBaseUrl =
      location.hostname === 'localhost' ? baseUrl : undefined

    const srcWithState = `${src}?${staticState.create({
      baseUrl: iframeBaseUrl,
    })}`

    const loaderRef = useRef<HTMLDivElement>(null)
    const handleLoad = () => {
      onLoad?.()
      loaderRef.current?.remove()
    }

    return (
      <div className="relative h-full w-full">
        <AppLoader ref={loaderRef} />
        <iframe
          ref={ref}
          id={id}
          src={srcWithState}
          title={name}
          data-testid="app-iframe"
          width="100%"
          height="100%"
          allow="geolocation *; clipboard-write"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
          allowFullScreen
          onLoad={handleLoad}
          className={cn('relative z-20', className)}
        />
      </div>
    )
  },
)
IframeAppForwardRef.displayName = 'IframeAppForwardRef'

const IframeAppMemorized = memo(IframeAppForwardRef)

IframeAppMemorized.displayName = 'IframeAppMemorized'

export const IframeAppContext = createContext<{
  isActive: boolean
}>({
  isActive: false,
})
export const IframeApp: FC<IframeAppProps> = ({
  id,
  src,
  name = 'Remote App',
  className,
  onLoad,
}) => {
  const { isActive } = useContext(IframeAppContext)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    isActive && setActiveIframe(iframeRef.current ?? null)
  }, [isActive])

  useHostStoreConnect(iframeRef, isActive, name)
  useHostFetch(iframeRef, isActive)
  useHostLocalStorage(iframeRef, isActive)
  useHostOpenapiHydrationCache(iframeRef, isActive)

  return (
    <IframeAppMemorized
      id={id}
      src={src}
      name={name}
      className={className}
      onLoad={onLoad}
      ref={iframeRef}
    />
  )
}
